import React from 'react';
import { Paper, Typography, Button, Stack, Box, Chip, LinearProgress, IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

import DicomDropzone from './DicomDropzone';

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'pseudonymized.dcm';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const statusColor = (status) => {
  if (status === 'success') return 'success';
  if (status === 'error') return 'error';
  if (status === 'uploading') return 'warning';
  return 'default';
};

const statusLabel = (status, t) => {
  if (status === 'success') return t('common.done', 'Hotovo');
  if (status === 'error') return t('common.error', 'Chyba');
  if (status === 'uploading') return t('common.processing', 'Spracovávam');
  return t('common.ready', 'Pripravené');
};

const DicomUploadPanel = ({ 
  fileItems, 
  setFileItems, 
  activeFileId, 
  setActiveFileId, 
  isProcessing, 
  hasPendingFiles, 
  onOpenOptions 
}) => {
  const { t } = useTranslation();

  const handleFilesAdded = (newFiles) => {
    const newItems = newFiles.map(f => ({
      id: crypto?.randomUUID?.() || String(Math.random()),
      name: f.name,
      file: f,
      status: 'ready',
      progress: 0,
      blob: null,
      downloadName: null,
      error: '',
    }));

    setFileItems(prev => [...prev, ...newItems]);
    
    // Auto-select the first newly added file if nothing is selected
    if (!activeFileId && newItems.length > 0) {
      setActiveFileId(newItems[0].id);
    }
  };

  const handleRemove = (e, id) => {
    e.stopPropagation(); // prevent setting active file when clicking delete
    if (isProcessing) return;
    
    setFileItems(prev => prev.filter(f => f.id !== id));
    if (activeFileId === id) {
      setActiveFileId(null);
    }
  };

  const handleDownloadAll = () => {
    const completedFiles = fileItems.filter(f => f.status === 'success' && f.blob);
    completedFiles.forEach(f => downloadBlob(f.blob, f.downloadName));
  };

  const anyCompleted = fileItems.some(f => f.status === 'success');

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {t('PseudoDicomPage.uploadTitle', 'Nahrať súbory')}
      </Typography>

      <DicomDropzone 
        disabled={isProcessing} 
        hasFile={false} // Allow dropping multiple times
        onFilesAdded={handleFilesAdded} 
      />

      {/* File List */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2, maxHeight: '350px' }}>
        {fileItems.map(item => (
          <Box 
            key={item.id}
            onClick={() => setActiveFileId(item.id)}
            sx={{ 
              mb: 1, p: 1.5, 
              borderRadius: 2, 
              border: '1px solid', 
              borderColor: activeFileId === item.id ? 'primary.main' : 'divider',
              bgcolor: activeFileId === item.id ? 'action.selected' : 'action.hover',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': { bgcolor: activeFileId === item.id ? 'action.selected' : 'action.focus' }
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Box sx={{ minWidth: 0, mr: 1, flex: 1 }}>
                <Typography variant="body2" noWrap title={item.name} sx={{ fontWeight: activeFileId === item.id ? 600 : 500 }}>
                  {item.name}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={statusLabel(item.status, t)} size="small" color={statusColor(item.status)} sx={{ borderRadius: 1.5 }} />
                {item.status === 'success' && item.blob && (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); downloadBlob(item.blob, item.downloadName); }}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton size="small" onClick={(e) => handleRemove(e, item.id)} disabled={isProcessing}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
            {item.status === 'uploading' && <LinearProgress variant="determinate" value={item.progress} sx={{ borderRadius: 2 }} />}
            {item.error && <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>{item.error}</Typography>}
          </Box>
        ))}
      </Box>

      {/* Actions */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button 
          variant="contained" 
          color="primary" 
          fullWidth 
          disableElevation
          disabled={!hasPendingFiles || isProcessing} 
          onClick={onOpenOptions}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {isProcessing ? t('common.processing', 'Spracovávam...') : t('PseudoFileUploadPanel.start', 'Spustiť pseudonymizáciu')}
        </Button>
        <Button 
          variant="outlined" 
          fullWidth 
          disabled={!anyCompleted} 
          onClick={handleDownloadAll} 
          startIcon={<DownloadIcon />}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {t('PseudoDicomPage.downloadAll', 'Stiahnuť hotové')}
        </Button>
      </Stack>
    </Paper>
  );
};

export default DicomUploadPanel;